import { defineClientConfig } from "@vuepress/client";
import * as Sentry from '@sentry/vue';
import Layout from "./layouts/Layout.vue";
import { createAuth0, authGuard } from '@auth0/auth0-vue';
import PrimeVue from 'primevue/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import Ripple from 'primevue/ripple';
import { Tooltip } from "primevue";
import { onMounted } from 'vue'
import SpotlightLayout from "./layouts/SpotlightLayout.vue";

import './styles/styles.css';

let auth0 = null;

const syncPrimeVueColorSchemeWithTemplate = () => {
  document.documentElement.classList = document.documentElement.attributes["data-theme"].value
}

if (!__VUEPRESS_SSR__) {
  auth0 = createAuth0({
    domain: "auth.funtrade.ch",
    clientId: "c1ptXw2NlwYtnHywLgi5xyn7YJ7Cj3aU",
    authorizationParams: {
      redirect_uri: window.location.origin + __VUEPRESS_BASE__ + "redirect/"
    }
  }, { skipRedirectCallback: true })
}

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: "#f0f3fe",
      100: "#e5ebfd",
      200: "#c7d4fa",
      300: "#acc0f8",
      400: "#8baaf5",
      500: "#6b97f3",
      600: "#3c82f0",
      700: "#275fb4",
      800: "#18417e",
      900: "#0a2349",
      950: "#041530",
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.900}',
        },
      }
    }
  }
});


export default defineClientConfig({
  async enhance({ app, router /*, siteData*/ }) {

    app.use(PrimeVue, {
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.dark'
        }
      },
      locale: {
        monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
        monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
      }
    });
    app.directive('ripple', Ripple);
    app.directive('tooltip', Tooltip);


    if (__VUEPRESS_SSR__ || __VUEPRESS_DEV__) return;

    app.use(
      auth0
    );

    router.getRoutes().forEach((route) => {
      if (route.path !== '/' && route.path !== '/index.html') {
        route.beforeEnter = authGuard;
      }
    });

    router.addRoute({
      path: '/redirect', name: 'redirect', component: undefined, beforeEnter: async (to) => {
        if (to.query.code) {
          try {
            const { appState } = await auth0.handleRedirectCallback();
            return appState?.target || "/"
          } catch (error) {
            return "/"
          }
        } else {
          return "/"
        }
      }
    });

    if (__VUEPRESS_DEV__) return;
    Sentry.init({
      app,
      dsn: "https://9c90b00e98ae41e3a57fd5215ea08547@o4504406161620992.ingest.sentry.io/4504406165946368",
      integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration(),],

      // Enable logs to be sent to Sentry
      enableLogs: true,
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for tracing.
      // We recommend adjusting this value in production
      // Learn more at
      // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
      tracesSampleRate: 1.0,
      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/docs\.funtrade\.ch\//],
      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      // Learn more at
      // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,

      environment: __VUEPRESS_DEV__ ? "development" : "production",
    });
  },
  setup() {

    onMounted(() => {
      syncPrimeVueColorSchemeWithTemplate()
      const colorModeButton = document.querySelector('.vp-toggle-color-mode-button');
      if (colorModeButton) {
        colorModeButton.addEventListener('click', () => {
          // Wait for VuePress to update the DOM, then sync PrimeVue
          setTimeout(() => {
            syncPrimeVueColorSchemeWithTemplate()
          }, 0);
        });
      }
    })
  },
  layouts: {
    Layout, SpotlightLayout,
  },
});
